import { useId } from 'react';
import { FLAG_PATHS, LANGUAGE_NAMES } from '../i18n/languages';
import type { Locale } from '../i18n';
import type { GateLanguage } from '../ConfiguratorRoot';

/**
 * The screen a multilingual Component shows before it shows itself.
 *
 * Opt-in per embed (`data-configento-language-select`), and only when the
 * Component actually carries more than one language — a gate in front of a
 * single-language configurator would be a click that asks nothing.
 *
 * The tiles are written in the languages on offer, not in the current UI
 * locale: this is the one screen whose reader has not told us what they read
 * yet. Hence "Deutsch" and "Français" rather than "German" and "French".
 */
export function LanguageGate({
    languages,
    title,
    onPick,
}: {
    languages: ReadonlyArray<GateLanguage>;
    /** The merchant's heading above the flags, shown as written. */
    title?: string;
    onPick: (code: string) => void;
}) {
    const heading = title?.trim();
    // Two configurators on one page may both ask.
    const headingId = useId();
    return (
        <div className="cfg-langgate" data-configento-state="language-select">
            {heading && <p className="cfg-langgate__title" id={headingId}>{heading}</p>}
            <div className="cfg-langgate__grid" role="group" aria-labelledby={heading ? headingId : undefined}>
                {languages.map(({ code, locale }) => (
                    <button
                        key={code}
                        type="button"
                        className="cfg-langgate__tile"
                        lang={code.slice(0, 2)}
                        // The accessible name is the language's own name, so a
                        // screen reader announces "Deutsch" and not "flag".
                        aria-label={LANGUAGE_NAMES[locale]}
                        data-locale={code}
                        onClick={() => onPick(code)}
                    >
                        <Flag locale={locale} />
                        <span className="cfg-langgate__name">{LANGUAGE_NAMES[locale]}</span>
                    </button>
                ))}
            </div>
        </div>
    );
}

/**
 * `dangerouslySetInnerHTML` is safe here and only here: the markup comes from
 * {@link FLAG_PATHS}, a frozen constant in this bundle. No component data, no
 * merchant input, nothing from the page ever reaches it.
 */
function Flag({ locale }: { locale: Locale }) {
    const flag = FLAG_PATHS[locale];
    return (
        <svg
            className="cfg-langgate__flag"
            viewBox={flag.viewBox}
            preserveAspectRatio="xMidYMid slice"
            aria-hidden="true"
            focusable="false"
            dangerouslySetInnerHTML={{ __html: flag.shapes }}
        />
    );
}
